import { ReactNode } from "react";
import { Icon, Typography } from "@/components/atoms";

/**
 * One icon + title/subtitle line in a Booking Summary box — the canonical row shared by every box
 * (Address / Trip Details / Payment) across both the draft-step and priced Payment-step variants, so
 * the summary reads identically on every step. Spec (from the Trip Details section): 20px (`md`) icon,
 * top-aligned, 12px semibold title over a 12px regular gray subtitle. Subtitle is optional.
 *
 * `subtitleTrailing` is an ADDITIVE slot for a second segment on the subtitle's own line — the design's
 * Trip Details node (`11784:130729`) puts "9:20 AM" and the pickup-delay caption side by side, and it is
 * the only node that lays the caption out horizontally rather than stacking it.
 *
 * Deliberately additive, and deliberately NOT a restyle. The spec above is shared with the Address and
 * Payment boxes, which no design node covers, so the design's other Trip Details changes (24px icons,
 * `items-center`, `#1D2939` sublines) would either break the "reads identically on every step"
 * invariant or silently restyle two boxes nobody drew. The horizontal wrapper is therefore emitted only
 * when there is something to put in it: a row with no trailing segment keeps the plain subtitle markup,
 * so the other boxes cannot shift by a pixel.
 *
 * A trailing segment renders even with no subtitle. `PricedBookingSummary` leaves `time` undefined
 * whenever the pickup date will not parse, while the caption is computed independently of the date — so
 * gating the slot on the subtitle would drop the caption on exactly the rows that still have one.
 */
export const DetailRow = ({
  icon,
  title,
  subtitle,
  subtitleTrailing,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  subtitleTrailing?: ReactNode;
}) => (
  <div className="flex items-start gap-2.5">
    <Icon name={icon} size="md" className="shrink-0 text-neutral-dark-gray" />
    <div className="flex min-w-0 flex-col gap-0.5">
      <Typography variant="small" className="block">
        {title}
      </Typography>
      {subtitleTrailing ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {subtitle && (
            <Typography variant="small-regular" className="block text-neutral-dark-gray">
              {subtitle}
            </Typography>
          )}
          {subtitleTrailing}
        </div>
      ) : (
        subtitle && (
          <Typography variant="small-regular" className="block text-neutral-dark-gray">
            {subtitle}
          </Typography>
        )
      )}
    </div>
  </div>
);
